import { useEffect, useState } from "react";
import client from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import AccessDenied from "@/components/AccessDenied";
import { KpiCard } from "@/components/KpiCard";
import { fmtNum } from "@/lib/format";

export default function OrgChart() {
    const [data, setData] = useState(null);
    const [denied, setDenied] = useState(false);
    useEffect(() => {
        client.get("/org/chart").then((r) => setData(r.data)).catch((e) => { if (e.response?.status === 403) setDenied(true); });
    }, []);
    if (denied) return <AccessDenied />;
    if (!data) return <div className="p-16 font-serif text-3xl text-fp-ink">Loading…</div>;

    return (
        <div>
            <PageHeader overline="Operational vs Legal Structure" title="Organisational Chart" description="Management (operational) hierarchy differs from the legal reporting structure — critical for family-owned conglomerates." />
            <div className="px-4 sm:px-6 lg:px-12 py-10 space-y-8">
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                    <KpiCard label="Total Entities" value={fmtNum(data.summary.total_entities)} />
                    <KpiCard label="Active" value={fmtNum(data.summary.active)} />
                    <KpiCard label="Dormant" value={fmtNum(data.summary.dormant)} />
                    <KpiCard label="Divisions" value={fmtNum(data.summary.divisions)} />
                    <KpiCard label="Legal Holdcos" value={fmtNum(data.summary.legal_holdcos)} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                        <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Management View</div>
                        <h3 className="font-serif text-2xl text-fp-ink mb-6">Operational Chart</h3>
                        <div className="space-y-5">
                            {data.operational.divisions.map((d) => {
                                const subs = data.operational.sub_divisions[d.id] || [];
                                const entities = data.operational.entities.filter((e) => e.division_id === d.id);
                                return (
                                    <div key={d.id} className="border-l-2 border-fp-ink pl-4">
                                        <div className="font-serif text-lg text-fp-ink">{d.name}</div>
                                        <div className="font-mono text-[10px] text-fp-muted uppercase tracking-widest mb-2">{d.code} · {entities.length} entities</div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {subs.map((s) => (
                                                <span key={s} className="px-2 py-0.5 bg-fp-panel font-sans text-xs text-fp-secondary">{s}</span>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                        <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Ownership View</div>
                        <h3 className="font-serif text-2xl text-fp-ink mb-6">Legal Holdco Structure</h3>
                        <div className="space-y-3">
                            {data.legal.holdcos.map((h) => {
                                const owned = data.legal.ownership.filter((o) => o.holdco_id === h.id);
                                return (
                                    <div key={h.id} className="border border-fp-border p-4 hover:border-fp-ink transition-colors">
                                        <div className="flex items-baseline justify-between">
                                            <div className="font-sans text-sm text-fp-ink font-medium">{h.name}</div>
                                            <div className="font-mono text-xs text-fp-muted">{h.jurisdiction}</div>
                                        </div>
                                        <div className="font-mono text-xs text-fp-secondary mt-1">{owned.length} entities · avg {Math.round(owned.reduce((s, o) => s + o.ownership_pct, 0) / (owned.length || 1))}% ownership</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
